import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTrips } from '../../context/TripsContext';
import { SuggestedExpensesProvider, useSuggestedExpenses } from '../../context/SuggestedExpensesContext';
import { AddFirstExpense } from './components/AddFirstExpense';
import { ReceiptUpload, ProcessingReceipts } from './components';
import { storageService, expensesService } from '../../services/supabase';
import { receiptProcessingService } from '../../services/receiptProcessing.service';
import { ProcessingReceipt, ExtractedExpenseData } from '../../types/receipt';
import { Card, CardContent } from "@/components/ui/card";

const AddExpensePageContent = (): React.ReactNode => {
  useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currentTrip } = useTrips();
  const { firebaseUser } = useAuth();
  const { setSuggestedFromReceipts } = useSuggestedExpenses();

  const [processingReceipts, setProcessingReceipts] = useState<ProcessingReceipt[]>([]);
  const [processedFileNames, setProcessedFileNames] = useState<Set<string>>(new Set());

  // Get default category from URL query parameter
  const defaultCategory = searchParams.get('category');

  // Check if any receipt is currently processing
  const isProcessingReceipt = processingReceipts.some(r => r.status === 'processing' || r.status === 'uploading');

  // Helper to feed all completed receipts into the suggested expenses context
  const syncCompletedToContext = useCallback((receipts: ProcessingReceipt[]) => {
    const completed = receipts.filter(r => r.status === 'completed');
    if (completed.length > 0) {
      setSuggestedFromReceipts(completed);
    }
  }, [setSuggestedFromReceipts]);

  // Handle files uploaded from dashboard
  const dashboardFilesHandled = useRef(false);
  useEffect(() => {
    const state = location.state as { uploadedFiles?: File[]; uploadedFile?: File; uploadedUrl?: string; isProcessing?: boolean } | null;

    if (state?.uploadedFiles && state.uploadedFiles.length > 0) {
      // Guard against StrictMode double-invocation: the navigate() below clears
      // router state too late for the second mount-effect run to see it
      if (dashboardFilesHandled.current) return;
      dashboardFilesHandled.current = true;
      // New multi-file flow from dashboard
      handleFilesSelected(state.uploadedFiles);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    // Legacy single-file flow (kept for backward compatibility)
    if (state?.uploadedFile && state?.uploadedUrl && state?.isProcessing) {
      const file = state.uploadedFile;
      const uploadedUrl = state.uploadedUrl;

      const receiptId = `receipt-${Date.now()}`;

      setProcessedFileNames(prev => new Set(prev).add(file.name));

      const processReceipt = async () => {
        // Check if this file was already loaded as an expense
        if (currentTrip?.id) {
          try {
            const existingNames = await expensesService.getTripExpenseFileNames(currentTrip.id);
            if (existingNames.includes(file.name)) {
              setProcessingReceipts([{
                id: receiptId,
                file,
                uploadedUrl,
                status: 'already_loaded'
              }]);
              return;
            }
          } catch {
            // Continue with processing if check fails
          }
        }

        const newReceipt: ProcessingReceipt = {
          id: receiptId,
          file,
          uploadedUrl,
          status: 'processing'
        };
        setProcessingReceipts([newReceipt]);

        try {
          const result = await receiptProcessingService.processReceipt(uploadedUrl, file.type);

          if (result.success && result.data) {
            const completedReceipt: ProcessingReceipt = {
              id: receiptId,
              file,
              uploadedUrl,
              status: 'completed',
              extractedData: result.data
            };
            setProcessingReceipts([completedReceipt]);
            syncCompletedToContext([completedReceipt]);
          } else {
            setProcessingReceipts([{
              id: receiptId,
              file,
              uploadedUrl,
              status: 'error',
              error: result.error || 'Failed to process receipt'
            }]);
          }
        } catch (error) {
          console.error('Error processing receipt:', error);
          setProcessingReceipts([{
            id: receiptId,
            file,
            uploadedUrl,
            status: 'error',
            error: 'Failed to process receipt'
          }]);
        }
      };

      processReceipt();
      navigate(location.pathname, { replace: true, state: {} });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, navigate, syncCompletedToContext, currentTrip?.id]);

  const processFile = async (file: File, receiptId: string) => {
    if (!firebaseUser) return;

    try {
      const uploadedUrl = await storageService.uploadReceipt(file, firebaseUser.uid);

      setProcessingReceipts(prev =>
        prev.map(r => r.id === receiptId ? { ...r, uploadedUrl, status: 'processing' } : r)
      );

      const result = await receiptProcessingService.processReceipt(uploadedUrl, file.type);

      if (result.success && result.data) {
        const completedReceipt: ProcessingReceipt = {
          id: receiptId,
          file,
          uploadedUrl,
          status: 'completed',
          extractedData: result.data
        };
        setProcessingReceipts(prev => {
          const updated = prev.map(r => r.id === receiptId ? completedReceipt : r);
          syncCompletedToContext(updated);
          return updated;
        });
      } else {
        setProcessingReceipts(prev =>
          prev.map(r =>
            r.id === receiptId
              ? { ...r, status: 'error', error: result.error || 'Failed to process receipt' }
              : r
          )
        );
      }
    } catch (error) {
      console.error('Error processing receipt:', error);
      setProcessingReceipts(prev =>
        prev.map(r =>
          r.id === receiptId
            ? { ...r, status: 'error', error: 'Failed to upload or process receipt' }
            : r
        )
      );
    }
  };

  const handleFilesSelected = async (files: File[]) => {
    if (!firebaseUser || !currentTrip?.id) return;

    // Fetch existing expense file names for this trip to check for duplicates
    let existingFileNames: Set<string>;
    try {
      const names = await expensesService.getTripExpenseFileNames(currentTrip.id);
      existingFileNames = new Set(names);
    } catch {
      existingFileNames = new Set();
    }

    const currentFileNames = new Set(processingReceipts.map(r => r.file.name));
    const seenInBatch = new Set<string>();
    const newReceipts: ProcessingReceipt[] = files.map((file, i) => {
      const isAlreadyLoaded = existingFileNames.has(file.name);
      const isDuplicate = currentFileNames.has(file.name) || processedFileNames.has(file.name) || seenInBatch.has(file.name);
      seenInBatch.add(file.name);
      return {
        id: `receipt-${Date.now()}-${i}`,
        file,
        status: isAlreadyLoaded ? 'already_loaded' as const : 'uploading' as const,
        isDuplicate: isDuplicate && !isAlreadyLoaded,
      };
    });

    setProcessedFileNames(prev => {
      const next = new Set(prev);
      files.forEach(f => next.add(f.name));
      return next;
    });

    setProcessingReceipts(prev => [...prev, ...newReceipts]);

    // Only process files that aren't already loaded as expenses
    newReceipts
      .filter(receipt => receipt.status !== 'already_loaded')
      .forEach((receipt) => {
        processFile(receipt.file, receipt.id);
      });
  };

  const handleReviewReceipt = (receipt: ProcessingReceipt, selectedItems?: number[]) => {
    const receiptWithSelection = {
      ...receipt,
      selectedItemIndices: selectedItems
    };
    // Update the receipt in the list so context picks it up
    setProcessingReceipts(prev =>
      prev.map(r => r.id === receipt.id ? receiptWithSelection : r)
    );
  };

  const handleRemoveProcessingReceipt = (receiptId: string) => {
    setProcessingReceipts(prev => prev.filter(r => r.id !== receiptId));
  };

  const handleMergeItems = (receiptId: string) => {
    // Use a new id so the context treats this as a fresh receipt (avoids stale id collision)
    const mergedReceiptId = `${receiptId}-merged-${Date.now()}`;
    setProcessingReceipts(prev => {
      const updated = prev.map(r => {
        if (r.id !== receiptId || !r.extractedData?.items || r.extractedData.items.length <= 1) return r;

        const items = r.extractedData.items;
        const merged: ExtractedExpenseData = {
          amount: items.reduce((sum, item) => sum + item.amount, 0),
          base_amount: items.some(i => i.base_amount) ? items.reduce((sum, i) => sum + (i.base_amount || 0), 0) : undefined,
          tax: items.some(i => i.tax) ? items.reduce((sum, i) => sum + (i.tax || 0), 0) : undefined,
          tip: items.some(i => i.tip) ? items.reduce((sum, i) => sum + (i.tip || 0), 0) : undefined,
          fees: items.some(i => i.fees) ? items.reduce((sum, i) => sum + (i.fees || 0), 0) : undefined,
          currency: items[0].currency,
          description: items.map(i => i.description).join(', '),
          category: items[0].category,
          expense_date: items[0].expense_date,
          merchant: items[0].merchant,
          has_multiple_items: false,
        };

        return { ...r, id: mergedReceiptId, extractedData: merged };
      });
      syncCompletedToContext(updated);
      return updated;
    });
  };

  const handleRetryProcessing = async (receiptId: string) => {
    if (!firebaseUser) return;

    const processingReceipt = processingReceipts.find(r => r.id === receiptId);
    if (!processingReceipt || !processingReceipt.uploadedUrl) return;

    try {
      const result = await receiptProcessingService.processReceipt(processingReceipt.uploadedUrl, processingReceipt.file.type);

      if (result.success && result.data) {
        setProcessingReceipts(prev =>
          prev.map(r => r.id === receiptId ? {
            id: receiptId,
            file: processingReceipt.file,
            uploadedUrl: processingReceipt.uploadedUrl,
            status: 'completed' as const,
            extractedData: result.data
          } : r)
        );
      } else {
        setProcessingReceipts(prev =>
          prev.map(r =>
            r.id === receiptId
              ? { ...r, status: 'error', error: result.error || 'Failed to process receipt' }
              : r
          )
        );
      }
    } catch (error) {
      console.error('Error retrying receipt processing:', error);
      setProcessingReceipts(prev =>
        prev.map(r =>
          r.id === receiptId
            ? { ...r, status: 'error', error: 'Failed to process receipt' }
            : r
        )
      );
    }
  };

  const handleRetryCurrentReceipt = async () => {
    const processingReceipt = processingReceipts.find(r => r.status === 'processing' || r.status === 'uploading');
    if (processingReceipt) {
      await handleRetryProcessing(processingReceipt.id);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardContent className="pt-6">
          <ReceiptUpload
            onFilesSelected={handleFilesSelected}
            disabled={isProcessingReceipt}
          />
        </CardContent>
      </Card>

      {processingReceipts.length > 0 && (
        <ProcessingReceipts
          receipts={processingReceipts}
          onReviewReceipt={handleReviewReceipt}
          onRemoveReceipt={handleRemoveProcessingReceipt}
          onRetryProcessing={handleRetryProcessing}
          onMergeItems={handleMergeItems}
        />
      )}

      <AddFirstExpense
        tripId={currentTrip?.id!}
        defaultCategory={defaultCategory || undefined}
        isProcessing={isProcessingReceipt}
        onRetryProcessing={handleRetryCurrentReceipt}
      />
    </div>
  );
};

export const AddExpensePage = (): React.ReactNode => {
  const { currentTrip } = useTrips();

  return (
    <SuggestedExpensesProvider tripId={currentTrip?.id!}>
      <AddExpensePageContent />
    </SuggestedExpensesProvider>
  );
};
